import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    useCompleteDDASigningSessionMutation,
    useGetDDASigningSessionMutation,
} from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { paymentMethods, stepIDs, stepStatus } from 'utils/constants';
import { useTunnelChangeHandlers, useTunnelNavigation, useYousign } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { SubmitButton, Loader } from 'components';
import TunnelStep from '../common/TunnelStep';
import { Box, Typography } from '@mui/material';

const iframeId = 'ap-iframe';

const DirectDebitAuthorization = props => {
    const { handleNextStep, userStep } = props;

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const navigateToStep = useTunnelNavigation();
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const [getDDASigningSession, { isLoading: buttonLoader, data: ddaSession }] =
        useGetDDASigningSessionMutation();
    const [completeDDASigningSession, { isLoading: sendLoader, isSuccess: signatureSuccessful }] =
        useCompleteDDASigningSessionMutation();

    const [frameError, setFrameError] = useState(false);
    const [signatureCancelled, setSignatureCancelled] = useState(false);

    const stepVerified = useMemo(() => userStep?.status === stepStatus.VERIFIED, [userStep]);
    const stepCompleted = useMemo(() => userStep?.status === stepStatus.COMPLETED, [userStep]);
    const usesCreditTransfer = useMemo(
        () =>
            tunnelSubscription?.bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER,
        [tunnelSubscription]
    );

    useEffect(() => {
        // when landing directly on the step
        if (usesCreditTransfer === true) {
            navigateToStep(stepIDs.SUBSCRIPTION_FORM_SIGNATURE);
        }
    }, [usesCreditTransfer, navigateToStep]);

    useEffect(() => {
        if (!stepVerified && !ddaSession && !stepCompleted && usesCreditTransfer === false) {
            getDDASigningSession();
        }
    }, [getDDASigningSession, stepVerified, ddaSession, stepCompleted, usesCreditTransfer]);

    const handleRetry = useCallback(() => {
        setSignatureCancelled(false);
        setFrameError(false);
        getDDASigningSession();
    }, [getDDASigningSession]);

    const status = useYousign({
        singleton: true,
        containerId: iframeId,
        signatureUrl: ddaSession?.signerUrl,
        onSuccess: () => completeDDASigningSession(() => handleNextStep(true)),
        onError: () => setFrameError(true),
        onCancel: () => setSignatureCancelled(true),
    });

    useEffect(
        () =>
            setTunnelChangesHandlers(
                afterSubmitCallback => () => afterSubmitCallback(),
                () => true,
                !!ddaSession
            ),
        [ddaSession, setTunnelChangesHandlers]
    );

    useEffect(() => {
        if (signatureSuccessful) {
            handleNextStep(true);
        }
    }, [signatureSuccessful, handleNextStep]);

    return (
        <TunnelStep maxWidth={960} title={texts.DIRECT_DEBIT_AUTHORIZATION}>
            <Loader loading={buttonLoader || status === 'loading'}>
                {signatureCancelled && (
                    <Typography variant="body2" align="center" gutterBottom>
                        {texts.AUTHORISATION_REFUSEE}
                    </Typography>
                )}
                {(stepVerified || stepCompleted) && (
                    <Typography variant="body2" align="center" gutterBottom>
                        {texts.AUTHORISATION_SUCCESS}
                    </Typography>
                )}
                <Box
                    id={iframeId}
                    sx={{
                        display:
                            ddaSession &&
                            !frameError &&
                            !signatureCancelled &&
                            !stepVerified &&
                            !stepCompleted
                                ? 'block'
                                : 'none',
                        width: '100%',
                        height: '595px',
                    }}
                />
                {frameError && (
                    <Typography variant="body2" align="center" gutterBottom>
                        {texts.SIGNATURE_ERROR}
                    </Typography>
                )}
                {frameError && (
                    <SubmitButton
                        text={texts.RETRY}
                        onClick={handleRetry}
                        loading={buttonLoader || sendLoader}
                    />
                )}
            </Loader>
        </TunnelStep>
    );
};

export default DirectDebitAuthorization;
